/**
 * Rikki Mode — Theme JS
 * High-end women's fashion | Den Haag
 */

'use strict';

const theme = window.theme || {};

const RikkiMode = {
  init() {
    this.lock.init();
    this.header.init();
    this.cart.init();
    this.search.init();
    this.accordion.init();
    this.productGallery.init();
    this.stickyATC.init();
    this.shippingProgress.init();
    this.mobileNav.init();
    this.filterSidebar.init();
    this.quantitySelector.init();
    this.toasts.init();
    this.variantSelector.init();
    this.recentlyViewed.init();
    this.recommendations.init();
    this.collectionSort.init();
    this.loadMore.init();
    this.cardSwatches.init();
    this.cardHoverImages.init();
    this.newBadges.init();
    this.sizeGuide.init();
    this.quickView.init();
    this.footerAccordion.init();
    this.ticker.init();
    this.helpWidget.init();
    this.newsletterPopup.init();
    this.scrollIndicator.init();
    this.scrollToTop.init();
    this.scrollRestore.init();
    this.formatCurrencies();
  },

  formatCurrencies() {
    document.querySelectorAll('[data-money]').forEach(el => {
      const cents = parseInt(el.dataset.money, 10);
      if (!isNaN(cents)) el.textContent = this.formatMoney(cents);
    });
  },

  formatMoney(cents) {
    return (cents / 100).toLocaleString('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    });
  },

  escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  },

  imageWidth(url, width) {
    if (!url) return '';
    const clean = String(url).split('?')[0];
    return `${clean}?width=${width}`;
  },

  isSizeOption(name) {
    const n = String(name || '').toLowerCase().trim();
    return n === 'mt' || n.includes('maat') || n.includes('size');
  },

  // Houd gelijk met snippets/size-sort-key.liquid.
  sizeRank(value, position = 0) {
    const letters = {
      xxxs: 10, '3xs': 10, xxs: 20, '2xs': 20, xs: 30, s: 40, m: 50, l: 60, xl: 70,
      xxl: 80, '2xl': 80, xxxl: 90, '3xl': 90, xxxxl: 100, '4xl': 100, '5xl': 110, '6xl': 120,
      onesize: 8000, 'one-size': 8000, os: 8000, uni: 8000, universeel: 8000, 'eénmaat': 8000, eenmaat: 8000
    };
    let v = String(value || '').toLowerCase().replace(/\s+/g, '');
    let bonus = 0;
    if (v.includes('/')) {
      bonus = 5;
      v = v.split('/')[0];
    }
    if (letters[v] !== undefined) return letters[v] + bonus;
    const num = Number(v.replace(',', '.'));
    if (v && !Number.isNaN(num) && num > 0 && num < 700) return 1000 + Math.round(num * 10) + bonus;
    return 9000 + position;
  },

  sortSizes(values) {
    return values
      .map((value, i) => ({ value, rank: this.sizeRank(value, i) }))
      .sort((a, b) => a.rank - b.rank)
      .map(item => item.value);
  }
};

/* ============================================================
   BODY SCROLL LOCK
   ============================================================ */

RikkiMode.lock = {
  count: 0,
  init() {},
  add() {
    this.count += 1;
    document.body.classList.add('is-locked');
  },
  remove() {
    this.count = Math.max(0, this.count - 1);
    if (this.count === 0) document.body.classList.remove('is-locked');
  },
  reset() {
    this.count = 0;
    document.body.classList.remove('is-locked');
  }
};

/* ============================================================
   HEADER — STICKY & SCROLL
   ============================================================ */

RikkiMode.header = {
  init() {
    this.el = document.querySelector('.site-header');
    this.announcement = document.querySelector('.announcement-bar');
    this.announcementSection = this.announcement?.closest('.shopify-section') || null;
    this.headerSection = this.el?.closest('.shopify-section') || null;
    this.group = document.getElementById('shopify-section-group-header-group')
      || document.querySelector('.shopify-section-group-header-group');
    if (!this.el) return;

    this.fitNav();
    this.setHeightVar();
    if (!this._resizeBound) {
      this._resizeBound = true;
      window.addEventListener('resize', () => {
        this.fitNav();
        this.setHeightVar();
      }, { passive: true });
      window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
      document.fonts?.ready.then(() => {
        this.fitNav();
        this.setHeightVar();
      });
    }

    if (this.el.dataset.transparent === 'true') {
      document.body.classList.add('header-transparent');
    } else {
      document.body.classList.remove('header-transparent');
    }

    this.handleScroll();
    this.bindDropdowns();
  },
  fitNav() {
    const el = document.querySelector('.site-header');
    const nav = el?.querySelector('.header__nav');
    const items = nav?.querySelectorAll('.header__nav-item');
    if (!nav || !items?.length) return;
    el.classList.remove('site-header--compact', 'site-header--no-labels');
    if (window.innerWidth < 992) return;

    const overflows = () => {
      const first = items[0].getBoundingClientRect();
      const last = items[items.length - 1].getBoundingClientRect();
      return (last.right - first.left) > nav.clientWidth + 1;
    };
    if (!overflows()) return;
    el.classList.add('site-header--no-labels');
    if (!overflows()) return;
    el.classList.add('site-header--compact');
  },
  bindDropdowns() {
    this.el.querySelectorAll('.header__nav-item.has-dropdown').forEach(item => {
      if (item.dataset.bound === 'true') return;
      item.dataset.bound = 'true';
      const link = item.querySelector(':scope > .header__nav-link');
      if (!link) return;
      let closeTimer = null;
      const HOVER_DELAY = 180;

      const open = () => {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
        this.el.querySelectorAll('.header__nav-item.has-dropdown.is-open').forEach(other => {
          if (other !== item) {
            other.classList.remove('is-open');
            other.querySelector(':scope > .header__nav-link')?.setAttribute('aria-expanded', 'false');
          }
        });
        item.classList.add('is-open');
        link.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        item.classList.remove('is-open');
        link.setAttribute('aria-expanded', 'false');
      };
      const scheduleClose = () => {
        if (closeTimer) clearTimeout(closeTimer);
        closeTimer = setTimeout(close, HOVER_DELAY);
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', scheduleClose);
      link.addEventListener('focus', open);
      link.addEventListener('click', e => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          if (!item.classList.contains('is-open')) {
            e.preventDefault();
            open();
          }
        }
      });
      link.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          close();
          link.focus();
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          open();
          item.querySelector('.dropdown-menu a, .mega-menu a')?.focus();
        }
      });
      item.querySelectorAll('.dropdown-menu a, .mega-menu a').forEach(sub => {
        sub.addEventListener('keydown', e => {
          if (e.key === 'Escape') {
            e.preventDefault();
            close();
            link.focus();
          }
        });
      });
    });

    if (!this._focusBound) {
      this._focusBound = true;
      document.addEventListener('focusin', e => {
        this.el?.querySelectorAll('.header__nav-item.has-dropdown.is-open').forEach(item => {
          if (!item.contains(e.target)) {
            item.classList.remove('is-open');
            item.querySelector(':scope > .header__nav-link')?.setAttribute('aria-expanded', 'false');
          }
        });
      });
      document.addEventListener('click', e => {
        if (!this.el?.contains(e.target)) {
          this.el?.querySelectorAll('.header__nav-item.has-dropdown.is-open').forEach(item => {
            item.classList.remove('is-open');
            item.querySelector(':scope > .header__nav-link')?.setAttribute('aria-expanded', 'false');
          });
        }
      });
      document.addEventListener('keydown', e => {
        if (e.key !== 'Escape') return;
        this.el?.querySelectorAll('.header__nav-item.has-dropdown.is-open').forEach(item => {
          item.classList.remove('is-open');
          const link = item.querySelector(':scope > .header__nav-link');
          link?.setAttribute('aria-expanded', 'false');
          link?.focus();
        });
      });
    }
  },
  setHeightVar() {
    const height = this.el?.offsetHeight || 72;
    const announcement = this.announcement || document.querySelector('.announcement-bar');
    const announcementVisible = !!(announcement && announcement.offsetParent !== null && announcement.offsetHeight > 0);
    const announcementHeight = announcementVisible ? Math.max(announcement.offsetHeight, 32) : 0;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
    document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
    document.documentElement.style.setProperty('--header-group-height', `${height + announcementHeight}px`);
    if (this.el) {
      this.el.style.position = 'sticky';
      this.el.style.top = `${announcementHeight}px`;
      this.el.style.zIndex = '500';
    }
    if (announcement) {
      announcement.style.position = 'sticky';
      announcement.style.top = '0';
      announcement.style.zIndex = '501';
    }
    document.body.style.paddingTop = '';
    const main = document.getElementById('main-content');
    if (main) main.style.paddingTop = '';
  },
  handleScroll() {
    if (!this.el) return;
    const scrolled = window.scrollY > 8;
    this.el.classList.toggle('scrolled', scrolled);
    document.body.classList.toggle('header-scrolled', scrolled);
  }
};

/* ============================================================
   MOBILE NAV
   ============================================================ */

RikkiMode.mobileNav = {
  init() {
    this.toggle = document.getElementById('mobile-nav-toggle');
    this.nav = document.getElementById('mobile-nav');
    this.close = document.getElementById('mobile-nav-close');
    this.overlay = document.getElementById('mobile-nav-overlay');
    this.submenuToggles = document.querySelectorAll('.mobile-nav__submenu-toggle');

    if (!this.toggle || !this.nav) return;

    if (this.overlay && this.overlay.parentElement !== document.body) {
      document.body.appendChild(this.overlay);
    }
    if (this.nav.parentElement !== document.body) {
      document.body.appendChild(this.nav);
    }

    if (this.toggle.dataset.bound === 'true') return;
    this.toggle.dataset.bound = 'true';

    this.toggle.addEventListener('click', () => {
      if (this.nav.classList.contains('is-open')) this.closeNav();
      else this.open();
    });
    if (this.close) this.close.addEventListener('click', () => this.closeNav());
    if (this.overlay) {
      this.overlay.removeAttribute('hidden');
      this.overlay.addEventListener('click', () => this.closeNav());
    }

    this.submenuToggles.forEach(btn => {
      btn.addEventListener('click', () => {
        const submenu = btn.nextElementSibling;
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        this.submenuToggles.forEach(other => {
          if (other !== btn) {
            other.setAttribute('aria-expanded', 'false');
            other.nextElementSibling?.classList.remove('is-open');
          }
        });
        btn.setAttribute('aria-expanded', String(!isOpen));
        if (submenu) submenu.classList.toggle('is-open', !isOpen);
      });
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.nav.classList.contains('is-open')) this.closeNav();
    });
  },
  trapFocus(e) {
    if (!this.nav?.classList.contains('is-open')) return;
    if (e.key !== 'Tab') return;
    const focusable = [...this.nav.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  },
  open() {
    this.nav.classList.add('is-open');
    this.nav.setAttribute('aria-hidden', 'false');
    this.toggle.classList.add('is-active');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.overlay?.classList.add('is-open');
    this.overlay?.removeAttribute('hidden');
    RikkiMode.lock.add();
    this._trap = e => this.trapFocus(e);
    document.addEventListener('keydown', this._trap);
    const first = this.nav.querySelector('a, button');
    first?.focus();
  },
  closeNav() {
    this.nav.classList.remove('is-open');
    this.nav.setAttribute('aria-hidden', 'true');
    this.toggle.classList.remove('is-active');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.overlay?.classList.remove('is-open');
    RikkiMode.lock.remove();
    if (this._trap) document.removeEventListener('keydown', this._trap);
    this.toggle.focus();
  }
};

/* ============================================================
   SEARCH OVERLAY + PREDICTIVE
   ============================================================ */

RikkiMode.search = {
  init() {
    this.overlay = document.getElementById('search-overlay');
    this.openBtn = document.getElementById('search-open');
    this.closeBtn = document.getElementById('search-close');
    this.input = document.getElementById('search-overlay-input');
    this.results = document.getElementById('search-predictive');
    this.timer = null;

    if (!this.overlay) return;
    if (this.overlay.dataset.bound === 'true') return;
    this.overlay.dataset.bound = 'true';

    if (this.openBtn) {
      this.openBtn.addEventListener('click', () => {
        if (this.openBtn.dataset.searchPage) {
          window.location.href = this.openBtn.dataset.searchPage;
          return;
        }
        this.open();
      });
    }
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    this.overlay.querySelector('.search-overlay__backdrop')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close();
    });

    if (this.input && theme.routes?.predictiveSearch) {
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        const q = this.input.value.trim();
        if (q.length < 2) {
          if (this.results) {
            this.results.hidden = true;
            this.results.innerHTML = '';
          }
          return;
        }
        this.timer = setTimeout(() => this.predict(q), 220);
      });
    }
  },
  open() {
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    RikkiMode.lock.add();
    setTimeout(() => this.input?.focus(), 80);
  },
  close() {
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    RikkiMode.lock.remove();
    this.openBtn?.focus();
  },
  async predict(query) {
    if (!this.results) return;
    try {
      const res = await fetch(`${theme.routes.predictiveSearch}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
      if (!res.ok) return;
      const data = await res.json();
      const products = data.resources?.results?.products || [];
      if (!products.length) {
        this.results.innerHTML = `<p class="search-predictive__empty">${theme.strings?.noResults || 'Geen producten gevonden'}</p>`;
        this.results.hidden = false;
        return;
      }
      this.results.innerHTML = products.map(p => {
        const img = p.image || p.featured_image?.url || p.featured_image || '';
        const imgSrc = img ? `${String(img).split('?')[0]}?width=112` : '';
        let priceCents = p.price;
        if (typeof priceCents === 'string') priceCents = Math.round(parseFloat(priceCents) * 100);
        const displayPrice = typeof priceCents === 'number' && !isNaN(priceCents)
          ? RikkiMode.formatMoney(priceCents)
          : '';
        return `
          <a class="search-predictive__item" href="${RikkiMode.escapeHtml(p.url || '#')}">
            ${imgSrc ? `<img src="${RikkiMode.escapeHtml(imgSrc)}" alt="" width="56" height="74" loading="lazy">` : '<span></span>'}
            <span class="search-predictive__title">${RikkiMode.escapeHtml(p.title || '')}</span>
            <span class="search-predictive__price">${RikkiMode.escapeHtml(displayPrice)}</span>
          </a>`;
      }).join('');
      this.results.hidden = false;
    } catch (e) {
      console.error(e);
    }
  }
};

/* ============================================================
   CART
   ============================================================ */

RikkiMode.cart = {
  drawer: null,
  overlay: null,
  closeBtn: null,
  countEls: [],
  updating: false,

  init() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.closeBtn = document.getElementById('cart-drawer-close');
    this.countEls = document.querySelectorAll('[data-cart-count]');
    this.threshold = theme.freeShippingThreshold || 7500;

    if (this._bound) {
      this.fetchCount();
      return;
    }
    this._bound = true;

    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

    document.querySelectorAll('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', e => {
        if (theme.cartType !== 'drawer' || !this.drawer) return;
        e.preventDefault();
        this.open();
      });
    });

    document.querySelectorAll('[data-add-to-cart-form]').forEach(form => {
      form.addEventListener('submit', e => this.handleAddToCart(e));
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.close();
    });

    document.querySelectorAll('[data-cart-qty-update]').forEach(btn => {
      btn.addEventListener('click', () => this.updateQuantity(btn));
    });

    document.querySelectorAll('[data-cart-remove]').forEach(btn => {
      btn.addEventListener('click', () => this.removeItem(btn));
    });

    this.fetchCount();
  },

  async fetchCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.updateCount(cart.item_count);
    } catch (e) {
      console.error('Cart fetch error:', e);
    }
  },

  updateCount(count) {
    this.countEls.forEach(el => {
      el.textContent = count;
      el.setAttribute('data-count', count);
    });
  },

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    RikkiMode.lock.add();
    this.renderDrawer();
  },

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    RikkiMode.lock.remove();
  },

  async renderDrawer() {
    const body = document.getElementById('cart-drawer-body');
    const footer = document.getElementById('cart-drawer-footer');
    if (!body) return;

    body.innerHTML = '<div style="padding:2rem;text-align:center;color:#999">Laden...</div>';

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      if (cart.item_count === 0) {
        body.innerHTML = `
          <div class="cart-drawer__empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            <p>${theme.strings?.cartEmpty || 'Je winkelmand is leeg'}</p>
            <a href="${theme.routes?.search ? '/collections/all' : '/collections/all'}" class="btn btn--primary btn--sm">${theme.strings?.continueShopping || 'Verder winkelen'}</a>
          </div>`;
        if (footer) footer.innerHTML = '';
        this.updateCount(0);
        return;
      }

      body.innerHTML = cart.items.map(item => `
        <div class="cart-item" data-line-key="${item.key}">
          <img class="cart-item__img" src="${RikkiMode.imageWidth(item.image, 160)}" alt="${RikkiMode.escapeHtml(item.product_title)}" width="100" height="133">
          <div class="cart-item__details">
            <div class="cart-item__title">${RikkiMode.escapeHtml(item.product_title)}</div>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${RikkiMode.escapeHtml(item.variant_title)}</div>` : ''}
            ${item.original_line_price > item.final_line_price ? `<div class="cart-item__variant"><s>${RikkiMode.formatMoney(item.original_line_price)}</s></div>` : ''}
            <div class="cart-item__price-row">
              <div class="cart-item__qty">
                <button type="button" class="cart-item__qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Minder">−</button>
                <span class="cart-item__qty-value">${item.quantity}</span>
                <button type="button" class="cart-item__qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Meer">+</button>
              </div>
              <span class="price">${RikkiMode.formatMoney(item.final_line_price)}</span>
            </div>
            <button type="button" class="cart-item__remove" data-key="${item.key}">Verwijderen</button>
          </div>
        </div>
      `).join('');

      body.querySelectorAll('.cart-item__qty-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const currentQty = parseInt(btn.dataset.qty, 10);
          const newQty = btn.dataset.action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
          this.updateLineQty(btn.dataset.key, newQty);
        });
      });

      body.querySelectorAll('.cart-item__remove').forEach(btn => {
        btn.addEventListener('click', () => this.updateLineQty(btn.dataset.key, 0));
      });

      const remaining = Math.max(0, this.threshold - cart.total_price);
      const pct = this.threshold > 0 ? Math.min(100, (cart.total_price / this.threshold) * 100) : 100;
      const freeShippingText = remaining > 0
        ? `Nog <strong>${RikkiMode.formatMoney(remaining)}</strong> verwijderd van gratis verzending`
        : 'Je hebt gratis verzending';
      const discountRow = cart.total_discount > 0
        ? `<div class="cart-summary__row"><span>Korting</span><span>−${RikkiMode.formatMoney(cart.total_discount)}</span></div>`
        : '';

      if (footer) {
        footer.innerHTML = `
          ${this.threshold > 0 ? `<div class="shipping-progress">
            <div class="shipping-progress__text">${freeShippingText}</div>
            <div class="shipping-progress__bar"><div class="shipping-progress__fill" style="width:${pct}%"></div></div>
          </div>` : ''}
          <div class="cart-summary__row">
            <span>Subtotaal</span>
            <span class="price">${RikkiMode.formatMoney(cart.total_price)}</span>
          </div>
          ${discountRow}
          <div style="font-size:0.75rem;color:#999;margin:0.5rem 0 1rem;text-align:center">Verzendkosten worden berekend bij het afrekenen</div>
          <a href="/checkout" class="btn btn--primary btn--full">${theme.strings?.checkout || 'Afrekenen'}</a>
          <div class="cart-drawer__payments"></div>
          <div class="cart-drawer__usps">Veilig betalen · Snelle levering · 14 dagen retour</div>
          ${theme.trustpilot?.enable && theme.trustpilot?.businessUnitId && (theme.trustpilot?.cartTemplateId || theme.trustpilot?.templateId) ? `
          <div class="cart-drawer__trustpilot trustpilot-stars" data-trustpilot-slot>
            <div
              class="trustpilot-widget"
              data-locale="nl-NL"
              data-template-id="${RikkiMode.escapeHtml(theme.trustpilot.cartTemplateId || theme.trustpilot.templateId)}"
              data-businessunit-id="${RikkiMode.escapeHtml(theme.trustpilot.businessUnitId)}"
              data-style-height="24px"
              data-style-width="100%"
              data-theme="light"
            ></div>
          </div>` : ''}
        `;
        const payTpl = document.getElementById('payment-icons-template');
        const payTarget = footer.querySelector('.cart-drawer__payments');
        if (payTpl && payTarget) payTarget.appendChild(payTpl.content.cloneNode(true));
        const tpWidget = footer.querySelector('.trustpilot-widget');
        if (tpWidget && window.Trustpilot) {
          window.Trustpilot.loadFromElement(tpWidget, true);
        }
      }

      this.updateCount(cart.item_count);
    } catch (e) {
      body.innerHTML = '<div style="padding:2rem;text-align:center;color:#c00">Er is een fout opgetreden</div>';
      console.error(e);
    }
  },

  async updateLineQty(key, qty) {
    if (this.updating) return;
    this.updating = true;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      const cart = await res.json();
      if (!res.ok || cart.status) {
        throw new Error(cart.description || cart.message || theme.strings?.cartError || 'Bijwerken mislukt');
      }
      this.updateCount(cart.item_count);
      this.renderDrawer();
      this.updateShippingProgress(cart);
    } catch (e) {
      RikkiMode.toasts.show(e.message || theme.strings?.cartError || 'Bijwerken mislukt', 'error');
    } finally {
      this.updating = false;
    }
  },

  async handleAddToCart(e) {
    e.preventDefault();
    const form = e.target;
    const btn = form.querySelector('[type="submit"]');
    if (!btn) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) {
      RikkiMode.toasts.show(theme.strings?.selectSize || 'Selecteer eerst een maat', 'error');
      return;
    }
    if (this.updating) return;
    this.updating = true;

    btn.classList.add('btn--loading');
    btn.disabled = true;
    const label = btn.querySelector('[data-atc-label]');
    const prev = label?.textContent;
    if (label) label.textContent = theme.strings?.adding || 'Bezig met toevoegen…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: parseInt(formData.get('quantity') || '1', 10)
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Er is iets misgegaan');
      }

      await res.json();
      await this.fetchCount();
      if (theme.cartType === 'drawer' && this.drawer) {
        this.open();
      }
      RikkiMode.toasts.show(theme.strings?.addedToCart || 'Toegevoegd aan winkelmand', 'success');
    } catch (err) {
      RikkiMode.toasts.show(err.message || 'Er is iets misgegaan', 'error');
    } finally {
      btn.classList.remove('btn--loading');
      btn.disabled = false;
      if (label && prev) label.textContent = prev;
      this.updating = false;
    }
  },

  updateShippingProgress(cart) {
    const pct = this.threshold > 0 ? Math.min(100, (cart.total_price / this.threshold) * 100) : 100;
    const fill = document.querySelector('.shipping-progress__fill');
    if (fill) fill.style.width = pct + '%';
  },

  async removeItem(btn) {
    const key = btn.dataset.key || btn.closest('[data-line-key]')?.dataset.lineKey;
    if (key) await this.updateLineQty(key, 0);
  },

  async updateQuantity(btn) {
    const row = btn.closest('.cart-item');
    const key = row?.dataset.lineKey;
    const qtyEl = row?.querySelector('.cart-item__qty-value');
    if (!key || !qtyEl) return;
    const currentQty = parseInt(qtyEl.textContent, 10);
    const newQty = btn.dataset.action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
    await this.updateLineQty(key, newQty);
  }
};

/* ============================================================
   ACCORDION
   ============================================================ */

RikkiMode.accordion = {
  init() {
    document.querySelectorAll('.accordion__trigger').forEach(trigger => {
      if (trigger.dataset.bound === 'true') return;
      trigger.dataset.bound = 'true';
      trigger.addEventListener('click', () => {
        const content = trigger.nextElementSibling;
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        const parent = trigger.closest('.accordion');
        if (parent) {
          parent.querySelectorAll('.accordion__trigger[aria-expanded="true"]').forEach(t => {
            if (t !== trigger) {
              t.setAttribute('aria-expanded', 'false');
              t.nextElementSibling?.classList.remove('is-open');
            }
          });
        }
        trigger.setAttribute('aria-expanded', String(!isOpen));
        content?.classList.toggle('is-open', !isOpen);
      });
    });
  }
};

/* ============================================================
   PRODUCT GALLERY — thumbs, zoom, swipe
   ============================================================ */

RikkiMode.productGallery = {
  init() {
    this.main = document.querySelector('.product-gallery__main');
    if (!this.main) return;
    this.slides = Array.from(this.main.querySelectorAll('.product-gallery__slide'));
    this.thumbs = Array.from(document.querySelectorAll('.product-gallery__thumb'));
    this.mainImg = document.getElementById('product-main-img') || this.slides[0]?.querySelector('.product-gallery__main-img');
    this.count = this.slides.length || this.thumbs.length;
    if (!this.count) return;

    this.index = Math.max(0, this.thumbs.findIndex(t => t.classList.contains('is-active')));
    if (this.index < 0) this.index = 0;

    this.thumbs.forEach((thumb, i) => {
      if (thumb.dataset.bound === 'true') return;
      thumb.dataset.bound = 'true';
      thumb.addEventListener('click', () => this.goTo(i));
      thumb.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.goTo(i);
        }
      });
    });

    this.bindSwipe();
    this.bindNav();
    this.bindZoom();
    this.bindLightbox();
    this.setupGalleryMode();
    this.bindInlineZoom();
    if (!this.modeBound) {
      this.modeBound = true;
      window.matchMedia('(max-width: 991px)').addEventListener('change', () => this.setupGalleryMode());
    }
  },

  isInlineGallery() {
    return window.matchMedia('(max-width: 991px)').matches;
  },

  setupGalleryMode() {
    if (!this.main || !this.slides.length) return;
    const inline = this.isInlineGallery();
    this.main.classList.toggle('is-inline-scroll', inline);
    if (inline) {
      this.slides.forEach((slide, idx) => {
        slide.hidden = false;
        slide.classList.toggle('is-active', idx === this.index);
      });
      if (this.main.dataset.inlineScrollBound !== 'true') {
        this.main.dataset.inlineScrollBound = 'true';
        this.main.addEventListener('scroll', () => this.syncFromScroll(), { passive: true });
      }
      this.scrollToIndex(this.index, false);
    } else {
      this.resetInlineZoom();
      this.slides.forEach((slide, idx) => {
        const active = idx === this.index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
    }
  },

  scrollToIndex(i, smooth = true) {
    if (!this.main?.classList.contains('is-inline-scroll')) return;
    const width = this.main.clientWidth;
    if (!width) return;
    const left = i * width;
    if (Math.abs(this.main.scrollLeft - left) < 2) return;
    this.main.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
  },

  syncFromScroll() {
    if (!this.main?.classList.contains('is-inline-scroll') || this.main.classList.contains('is-pinched')) return;
    const width = this.main.clientWidth || 1;
    const i = Math.max(0, Math.min(this.count - 1, Math.round(this.main.scrollLeft / width)));
    if (i === this.index) return;
    this.index = i;
    this.slides.forEach((slide, idx) => slide.classList.toggle('is-active', idx === this.index));
    this.mainImg = this.slides[this.index]?.querySelector('.product-gallery__main-img') || this.mainImg;
    this.markChrome();
  },

  markChrome() {
    this.thumbs.forEach((t, idx) => t.classList.toggle('is-active', idx === this.index));
    document.querySelectorAll('.product-gallery__dot').forEach((dot, di) => {
      dot.classList.toggle('is-active', di === this.index);
    });
  },

  goTo(i) {
    if (this.count < 1) return;
    this.index = ((i % this.count) + this.count) % this.count;
    const inline = this.main.classList.contains('is-inline-scroll');

    if (this.slides.length) {
      this.slides.forEach((slide, idx) => {
        const active = idx === this.index;
        if (!inline) slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      this.mainImg = this.slides[this.index].querySelector('.product-gallery__main-img');
      if (inline) {
        this.resetInlineZoom();
        this.scrollToIndex(this.index);
      }
    } else if (this.thumbs[this.index]?.dataset.src && this.mainImg) {
      const thumb = this.thumbs[this.index];
      this.mainImg.src = thumb.dataset.src;
      this.mainImg.srcset = thumb.dataset.srcset || thumb.dataset.src;
      this.mainImg.alt = thumb.dataset.alt || '';
      if (thumb.dataset.zoomSrc) this.mainImg.dataset.zoomSrc = thumb.dataset.zoomSrc;
    }

    this.markChrome();
    this.hideZoom();
  },

  bindZoom() {
    this.gallery = this.main.closest('.product-gallery');
    this.lens = this.main.querySelector('.product-zoom-lens');
    this.pane = this.gallery?.querySelector('.product-zoom-pane');
    const enabled = this.main.hasAttribute('data-zoom-enabled') && theme.enableZoom !== false;
    if (!enabled || !this.lens || !this.pane || this.main.dataset.zoomBound === 'true') return;
    if (window.innerWidth < 992) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 992px)').matches) return;

    this.main.dataset.zoomBound = 'true';
    this.main.addEventListener('mouseenter', e => this.showZoom(e));
    this.main.addEventListener('mousemove', e => this.moveZoom(e));
    this.main.addEventListener('mouseleave', () => this.hideZoom());
  },

  zoomSrc() {
    return this.mainImg?.dataset.zoomSrc || this.mainImg?.currentSrc || this.mainImg?.src || '';
  },

  /* Zichtbare bitmap bij object-fit: cover (inclusief bijgesneden randen). */
  paintedRect(img) {
    const box = img.getBoundingClientRect();
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh || !box.width || !box.height) {
      return { left: box.left, top: box.top, width: box.width, height: box.height, box };
    }

    const scale = Math.max(box.width / nw, box.height / nh);
    const width = nw * scale;
    const height = nh * scale;

    const pos = getComputedStyle(img).objectPosition.split(' ');
    const parsePos = (value, view, painted) => {
      const raw = value || '50%';
      if (raw.endsWith('%')) return (view - painted) * (parseFloat(raw) / 100);
      return parseFloat(raw) || 0;
    };

    return {
      left: box.left + parsePos(pos[0], box.width, width),
      top: box.top + parsePos(pos[1], box.height, height),
      width,
      height,
      box
    };
  },

  showZoom(e) {
    if (!this.mainImg || this.slides[this.index]?.querySelector('.product-gallery__video, .product-gallery__external')) return;
    const src = this.zoomSrc();
    if (!src) return;
    this.lens.hidden = false;
    this.pane.hidden = false;
    this.gallery?.classList.add('is-zoom-active');
    if (this.pane.dataset.src !== src) {
      this.pane.style.backgroundImage = `url("${src}")`;
      this.pane.dataset.src = src;
    }
    this.moveZoom(e);
  },

  hideZoom() {
    this.gallery?.classList.remove('is-zoom-active');
    if (this.lens) this.lens.hidden = true;
    if (this.pane) this.pane.hidden = true;
  },

  moveZoom(e) {
    if (!this.mainImg || !this.lens || !this.pane || this.lens.hidden) return;

    const img = this.mainImg;
    const painted = this.paintedRect(img);
    const mainRect = this.main.getBoundingClientRect();
    const galleryRect = this.gallery.getBoundingClientRect();
    const zoomLevel = 1.6;

    const visLeft = Math.max(painted.left, painted.box.left);
    const visTop = Math.max(painted.top, painted.box.top);
    const visRight = Math.min(painted.left + painted.width, painted.box.right);
    const visBottom = Math.min(painted.top + painted.height, painted.box.bottom);

    const paneSize = Math.min(mainRect.height, Math.max(280, mainRect.width * 0.9));
    const lensSize = Math.round(Math.min(paneSize / zoomLevel, visRight - visLeft, visBottom - visTop));
    const half = lensSize / 2;
    this.lens.style.width = `${lensSize}px`;
    this.lens.style.height = `${lensSize}px`;

    let cx = e.clientX;
    let cy = e.clientY;
    cx = Math.max(visLeft + half, Math.min(cx, visRight - half));
    cy = Math.max(visTop + half, Math.min(cy, visBottom - half));

    this.lens.style.left = `${cx - half - mainRect.left}px`;
    this.lens.style.top = `${cy - half - mainRect.top}px`;

    this.pane.style.width = `${paneSize}px`;
    this.pane.style.height = `${paneSize}px`;
    this.pane.style.top = `${mainRect.top - galleryRect.top}px`;
    this.pane.style.left = `${mainRect.right - galleryRect.left + 12}px`;

    const scale = paneSize / lensSize;
    const originX = cx - painted.left - half;
    const originY = cy - painted.top - half;
    this.pane.style.backgroundSize = `${painted.width * scale}px ${painted.height * scale}px`;
    this.pane.style.backgroundPosition = `${-originX * scale}px ${-originY * scale}px`;
  },

  bindLightbox() {
    this.lightbox = document.getElementById('product-lightbox');
    if (!this.lightbox || this.main.dataset.lightboxBound === 'true') return;
    this.main.dataset.lightboxBound = 'true';
    this.main.addEventListener('click', e => {
      if (this.isInlineGallery() || this.main.classList.contains('is-inline-scroll')) return;
      if (e.target.closest('.product-gallery__nav')) return;
      if (this.gallery?.classList.contains('is-zoom-active')) return;
      if (!this.mainImg) return;
      this.openLightbox();
    });
    this.lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.closeLightbox());
    this.lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => {
      this.goTo(this.index - 1);
      this.syncLightbox();
    });
    this.lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => {
      this.goTo(this.index + 1);
      this.syncLightbox();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.lightbox && !this.lightbox.hidden) this.closeLightbox();
    });
    this.bindLightboxGestures();
  },

  bindLightboxGestures() {
    const img = this.lightbox?.querySelector('.product-lightbox__img');
    if (!img || img.dataset.gesturesBound === 'true') return;
    img.dataset.gesturesBound = 'true';
    let startX = 0;
    let startY = 0;
    let scale = 1;
    let startDist = 0;

    const getDist = touches => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    img.addEventListener('touchstart', e => {
      if (e.touches.length === 1) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      } else if (e.touches.length === 2) {
        startDist = getDist(e.touches);
      }
    }, { passive: true });

    img.addEventListener('touchmove', e => {
      if (e.touches.length === 2 && startDist) {
        e.preventDefault();
        const next = Math.min(3, Math.max(1, getDist(e.touches) / startDist * scale));
        img.style.transform = `scale(${next})`;
        img.dataset.liveScale = String(next);
      }
    }, { passive: false });

    img.addEventListener('touchend', e => {
      if (img.dataset.liveScale) {
        scale = parseFloat(img.dataset.liveScale) || 1;
        delete img.dataset.liveScale;
      }
      if (e.changedTouches.length === 1 && scale <= 1.05) {
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) {
            this.goTo(this.index + 1);
          } else {
            this.goTo(this.index - 1);
          }
          scale = 1;
          img.style.transform = '';
          this.syncLightbox();
        }
      }
    }, { passive: true });
  },

  openLightbox() {
    if (!this.lightbox) return;
    const img = this.lightbox.querySelector('.product-lightbox__img');
    if (img) img.style.transform = '';
    this.syncLightbox();
    this.lightbox.hidden = false;
    RikkiMode.lock.add();
  },

  closeLightbox() {
    if (!this.lightbox) return;
    const img = this.lightbox.querySelector('.product-lightbox__img');
    if (img) img.style.transform = '';
    this.lightbox.hidden = true;
    RikkiMode.lock.remove();
  },

  syncLightbox() {
    const img = this.lightbox?.querySelector('.product-lightbox__img');
    if (!img) return;
    img.src = this.zoomSrc();
    img.alt = this.mainImg?.alt || '';
    img.style.transform = '';
  },

  bindNav() {
    const prev = document.querySelector('[data-gallery-prev]');
    const next = document.querySelector('[data-gallery-next]');
    if (prev && prev.dataset.bound !== 'true') {
      prev.dataset.bound = 'true';
      prev.addEventListener('click', () => this.goTo(this.index - 1));
    }
    if (next && next.dataset.bound !== 'true') {
      next.dataset.bound = 'true';
      next.addEventListener('click', () => this.goTo(this.index + 1));
    }
  },

  bindSwipe() {
    if (!this.main || this.count < 2 || this.main.dataset.swipeBound === 'true') return;
    this.main.dataset.swipeBound = 'true';
    let startX = 0;
    this.main.addEventListener('touchstart', e => {
      startX = e.changedTouches[0].screenX;
    }, { passive: true });
    this.main.addEventListener('touchend', e => {
      if (this.main.classList.contains('is-inline-scroll')) return;
      const dx = e.changedTouches[0].screenX - startX;
      if (Math.abs(dx) < 40) return;
      if (dx < 0) this.goTo(this.index + 1);
      else this.goTo(this.index - 1);
    }, { passive: true });
  },

  resetInlineZoom() {
    this.slides.forEach(slide => {
      const img = slide.querySelector('.product-gallery__main-img');
      if (img) img.style.transform = '';
      slide.classList.remove('is-pinched');
    });
    this.main?.classList.remove('is-pinched');
  },

  bindInlineZoom() {
    const dist = touches => Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    );
    const mid = touches => ({
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    });

    this.slides.forEach(slide => {
      const img = slide.querySelector('.product-gallery__main-img');
      if (!img || img.dataset.inlineZoom === 'true') return;
      img.dataset.inlineZoom = 'true';

      const state = {
        scale: 1,
        x: 0,
        y: 0,
        startDist: 0,
        startScale: 1,
        startX: 0,
        startY: 0,
        anchorX: 0,
        anchorY: 0,
        panX: 0,
        panY: 0,
        lastTap: 0,
        moved: false
      };

      const framePoint = (clientX, clientY) => {
        const rect = slide.getBoundingClientRect();
        return {
          x: clientX - rect.left - rect.width / 2,
          y: clientY - rect.top - rect.height / 2
        };
      };

      const apply = () => {
        img.style.transformOrigin = 'center center';
        img.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;
        const zoomed = state.scale > 1.02;
        slide.classList.toggle('is-pinched', zoomed);
        const anyZoomed = this.slides.some(item => item.classList.contains('is-pinched'));
        this.main.classList.toggle('is-pinched', anyZoomed);
      };

      const clamp = () => {
        if (state.scale <= 1) {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
          return;
        }
        state.scale = Math.min(3, state.scale);
        const rect = slide.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const maxX = ((state.scale - 1) * rect.width) / 2;
        const maxY = ((state.scale - 1) * rect.height) / 2;
        state.x = Math.max(-maxX, Math.min(maxX, state.x));
        state.y = Math.max(-maxY, Math.min(maxY, state.y));
      };

      const zoomAround = (clientX, clientY, scale) => {
        const point = framePoint(clientX, clientY);
        state.scale = scale;
        state.x = point.x - state.anchorX * scale;
        state.y = point.y - state.anchorY * scale;
        clamp();
      };

      const rememberAnchor = (clientX, clientY) => {
        const point = framePoint(clientX, clientY);
        const safeScale = state.scale || 1;
        state.anchorX = (point.x - state.x) / safeScale;
        state.anchorY = (point.y - state.y) / safeScale;
      };

      slide.addEventListener('touchstart', e => {
        if (!this.main.classList.contains('is-inline-scroll')) return;
        state.moved = false;
        if (e.touches.length >= 2) {
          e.preventDefault();
          const point = mid(e.touches);
          state.startDist = dist(e.touches);
          state.startScale = state.scale;
          rememberAnchor(point.x, point.y);
        } else if (e.touches.length === 1) {
          state.panX = e.touches[0].clientX;
          state.panY = e.touches[0].clientY;
          state.startX = state.x;
          state.startY = state.y;
        }
      }, { passive: false });

      slide.addEventListener('touchmove', e => {
        if (!this.main.classList.contains('is-inline-scroll')) return;
        if (e.touches.length >= 2 && state.startDist) {
          e.preventDefault();
          state.moved = true;
          const point = mid(e.touches);
          const next = state.startScale * (dist(e.touches) / state.startDist);
          zoomAround(point.x, point.y, next);
          apply();
        } else if (e.touches.length === 1 && state.scale > 1.02) {
          const dx = e.touches[0].clientX - state.panX;
          const dy = e.touches[0].clientY - state.panY;
          if (Math.hypot(dx, dy) > 6) state.moved = true;
          e.preventDefault();
          state.x = state.startX + dx;
          state.y = state.startY + dy;
          clamp();
          apply();
        }
      }, { passive: false });

      slide.addEventListener('touchend', e => {
        if (!this.main.classList.contains('is-inline-scroll')) return;
        if (e.touches.length >= 2) return;
        if (e.touches.length === 1) {
          state.panX = e.touches[0].clientX;
          state.panY = e.touches[0].clientY;
          state.startX = state.x;
          state.startY = state.y;
          state.startDist = 0;
          return;
        }

        const touch = e.changedTouches[0];
        const dx = Math.abs((touch?.clientX || 0) - state.panX);
        const dy = Math.abs((touch?.clientY || 0) - state.panY);
        const now = Date.now();
        if (!state.moved && dx < 14 && dy < 14) {
          if (now - state.lastTap < 300) {
            if (state.scale > 1.02) {
              state.scale = 1;
              state.x = 0;
              state.y = 0;
            } else if (touch) {
              rememberAnchor(touch.clientX, touch.clientY);
              zoomAround(touch.clientX, touch.clientY, 2);
            }
            apply();
            state.lastTap = 0;
          } else {
            state.lastTap = now;
          }
        }
        if (state.scale < 1.05) {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
          apply();
        }
        state.startDist = 0;
      }, { passive: true });
    });
  }
};

/* ============================================================
   STICKY ADD TO CART (MOBILE)
   ============================================================ */

RikkiMode.stickyATC = {
  init() {
    this.addToCartSection = document.querySelector('.add-to-cart-form');
    this.stickyBar = document.getElementById('sticky-atc');
    this.stickyBtn = document.getElementById('sticky-atc-btn');
    if (!this.addToCartSection || !this.stickyBar) return;

    const target = this.addToCartSection.querySelector('.btn-add-to-cart') || this.addToCartSection;
    const update = () => {
      const rect = target.getBoundingClientRect();
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 0;
      const show = rect.bottom < header + 4;
      this.stickyBar.classList.toggle('is-visible', show);
      this.stickyBar.setAttribute('aria-hidden', String(!show));
      document.body.classList.toggle('has-sticky-atc', show);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    this.stickyBtn?.addEventListener('click', () => {
      const selected = RikkiMode.variantSelector.selectedOptions?.() || [];
      const complete = !selected.length || selected.every(Boolean);
      if (!complete) {
        this.addToCartSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.addToCartSection.querySelector('.size-option:not(.is-active)')?.focus();
        RikkiMode.toasts.show(theme.strings?.selectSize || 'Selecteer eerst een maat', 'error');
        return;
      }
      const formBtn = this.addToCartSection.querySelector('.btn-add-to-cart');
      if (formBtn) formBtn.click();
      else this.addToCartSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
};

/* ============================================================
   SHIPPING PROGRESS BAR
   ============================================================ */

RikkiMode.shippingProgress = {
  init() {
    this.threshold = theme.freeShippingThreshold || 7500;
    this.bar = document.querySelector('.shipping-progress__fill');
    this.text = document.querySelector('.shipping-progress__text');
    if (!this.bar) return;
    const cartTotal = parseInt(this.bar.closest('[data-cart-total]')?.dataset.cartTotal || '0', 10);
    this.update(cartTotal);
  },
  update(total) {
    if (!this.bar) return;
    const pct = this.threshold > 0 ? Math.min(100, (total / this.threshold) * 100) : 100;
    this.bar.style.width = pct + '%';
    if (this.text) {
      const remaining = Math.max(0, this.threshold - total);
      if (remaining > 0) {
        this.text.innerHTML = `Nog <strong>${RikkiMode.formatMoney(remaining)}</strong> voor gratis verzending`;
      } else {
        this.text.textContent = 'Je hebt gratis verzending';
        this.text.style.color = 'var(--color-success)';
      }
    }
  }
};

/* ============================================================
   FILTER SIDEBAR (MOBILE TOGGLE)
   ============================================================ */

RikkiMode.filterSidebar = {
  init() {
    this.toggle = document.getElementById('filter-sidebar-toggle');
    this.sidebar = document.getElementById('filter-sidebar');
    this.closeBtn = document.getElementById('filter-sidebar-close');
    if (!this.sidebar) return;

    this.placeholder = document.createComment('filter-sidebar-anchor');
    this.sidebar.parentNode.insertBefore(this.placeholder, this.sidebar);
    this.relocate();
    if (!this._resizeBound) {
      this._resizeBound = true;
      window.addEventListener('resize', () => this.relocate(), { passive: true });
    }

    this.toggle?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.getElementById('filter-overlay')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.sidebar.classList.contains('is-open')) this.close();
    });

    document.querySelectorAll('.filter-group__title').forEach(title => {
      title.addEventListener('click', () => {
        const group = title.closest('.filter-group');
        const options = group?.querySelector('.filter-group__body');
        if (!options) return;
        const hidden = options.hasAttribute('hidden');
        if (hidden) options.removeAttribute('hidden');
        else options.setAttribute('hidden', '');
        title.setAttribute('aria-expanded', String(hidden));
      });
    });

    const form = this.sidebar.querySelector('[data-filter-form]');
    if (form) {
      form.querySelectorAll('input, select').forEach(input => {
        input.addEventListener('change', () => form.submit());
      });
    }
  },
  relocate() {
    const overlay = document.getElementById('filter-overlay');
    const mobile = window.matchMedia('(max-width: 991px)').matches;
    if (!this.placeholder?.parentNode) return;
    const parent = this.placeholder.parentNode;
    if (mobile) {
      if (overlay && overlay.parentElement !== document.body) document.body.appendChild(overlay);
      if (this.sidebar.parentElement !== document.body) document.body.appendChild(this.sidebar);
    } else {
      parent.insertBefore(this.sidebar, this.placeholder.nextSibling);
      if (overlay) parent.insertBefore(overlay, this.sidebar);
    }
  },
  open() {
    this.relocate();
    this.sidebar.classList.add('is-open');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.getElementById('filter-overlay')?.classList.add('is-open');
    RikkiMode.lock.add();
  },
  close() {
    this.sidebar.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.getElementById('filter-overlay')?.classList.remove('is-open');
    RikkiMode.lock.remove();
  }
};

/* ============================================================
   QUANTITY SELECTOR
   ============================================================ */

RikkiMode.quantitySelector = {
  init() {
    document.querySelectorAll('.quantity-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.quantity-selector')?.querySelector('.quantity-input');
        if (!input) return;
        const current = parseInt(input.value, 10) || 1;
        const max = parseInt(input.max, 10) || Infinity;
        input.value = btn.dataset.action === 'decrease' ? Math.max(1, current - 1) : Math.min(max, current + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
};

/* ============================================================
   VARIANT SELECTOR
   ============================================================ */

RikkiMode.variantSelector = {
  init() {
    const form = document.querySelector('[data-add-to-cart-form]');
    if (!form) return;

    this.form = form;
    this.variantInput = form.querySelector('[name="id"]');
    this.optionInputs = form.querySelectorAll('[data-option-name]');
    this.priceEl = document.querySelector('[data-product-price]');
    this.comparePriceEl = document.querySelector('[data-product-compare-price]');
    this.atcBtn = form.querySelector('.btn-add-to-cart, [type="submit"]');
    this.variants = window.productVariants || [];

    form.querySelectorAll('.size-option, .color-option, [data-option-value]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled || btn.classList.contains('is-invalid') || btn.getAttribute('aria-disabled') === 'true') {
          return;
        }
        const group = btn.closest('.variant-selector');
        group?.querySelectorAll('.is-active').forEach(b => {
          if (b.parentElement === btn.parentElement) {
            b.classList.remove('is-active');
            b.setAttribute('aria-pressed', 'false');
          }
        });
        btn.parentElement?.querySelectorAll('.is-active').forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        const label = group?.querySelector('[data-selected-option]');
        if (label) label.textContent = btn.dataset.value;
        this.updateVariant();
      });
    });

    this.updateVariant();
  },

  selectedOptions() {
    const selected = [];
    this.form.querySelectorAll('.variant-selector').forEach(group => {
      const active = group.querySelector('.is-active[data-value]');
      selected.push(active ? active.dataset.value : null);
    });
    return selected;
  },

  updateVariant() {
    const selected = this.selectedOptions();
    const matched = this.variants.find(v => {
      const opts = v.options || [];
      return selected.every((val, i) => !val || opts[i] === val);
    });

    this.updateOptionAvailability(selected);

    if (!this.variantInput) return;
    const complete = selected.every(Boolean);

    if (!matched || !complete) {
      if (this.atcBtn) {
        this.atcBtn.disabled = true;
        const label = this.atcBtn.querySelector('[data-atc-label]');
        if (label) label.textContent = theme.strings?.selectSize || 'Selecteer een maat';
      }
      this.updateBackInStock(null, true);
      return;
    }

    this.variantInput.value = matched.id;

    if (this.priceEl) this.priceEl.textContent = RikkiMode.formatMoney(matched.price);

    if (this.comparePriceEl) {
      if (matched.compare_at_price && matched.compare_at_price > matched.price) {
        this.comparePriceEl.textContent = RikkiMode.formatMoney(matched.compare_at_price);
        this.comparePriceEl.hidden = false;
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.hidden = true;
        this.comparePriceEl.style.display = 'none';
      }
    }

    const available = matched.available;
    if (this.atcBtn) {
      this.atcBtn.disabled = !available;
      this.atcBtn.setAttribute('aria-disabled', String(!available));
      const label = this.atcBtn.querySelector('[data-atc-label]');
      if (label) {
        label.textContent = available
          ? (theme.strings?.addToCart || 'In winkelmand')
          : (theme.strings?.soldOut || 'Uitverkocht');
      }
    }

    this.updateBackInStock(matched, available);

    const stickyPrice = document.querySelector('.sticky-atc__price');
    if (stickyPrice) stickyPrice.textContent = RikkiMode.formatMoney(matched.price);
    const stickyVariant = document.querySelector('[data-sticky-variant]');
    if (stickyVariant && matched.title && matched.title !== 'Default Title') stickyVariant.textContent = matched.title;
    const stickyBtn = document.getElementById('sticky-atc-btn');
    if (stickyBtn) stickyBtn.textContent = available
      ? (theme.strings?.addToCart || 'In winkelmand')
      : (theme.strings?.soldOut || 'Uitverkocht');

    if (matched.featured_image) {
      const matchThumb = this.findThumbForImage(matched.featured_image);
      if (matchThumb && RikkiMode.productGallery?.goTo) {
        const thumbs = Array.from(document.querySelectorAll('.product-gallery__thumb'));
        const idx = thumbs.indexOf(matchThumb);
        if (idx >= 0) RikkiMode.productGallery.goTo(idx);
      }
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', matched.id);
      history.replaceState({}, '', url.toString());
    } catch (e) {}
  },

  updateOptionAvailability(selected) {
    this.form.querySelectorAll('.variant-selector').forEach((group, groupIndex) => {
      group.querySelectorAll('[data-value]').forEach(btn => {
        const trial = selected.slice();
        trial[groupIndex] = btn.dataset.value;
        const exists = this.variants.some(v => trial.every((val, i) => !val || v.options[i] === val));
        const available = this.variants.some(v => v.available && trial.every((val, i) => !val || v.options[i] === val));
        btn.classList.toggle('is-invalid', !exists);
        btn.classList.toggle('is-unavailable', exists && !available);
        if (btn.classList.contains('size-option') || btn.classList.contains('color-option')) {
          const block = !exists;
          btn.disabled = block && btn.classList.contains('size-option');
          btn.setAttribute('aria-disabled', String(block && btn.classList.contains('size-option')));
        }
      });
    });
  },

  findThumbForImage(image) {
    if (!image) return null;
    const id = image.id || image;
    return document.querySelector(`.product-gallery__thumb[data-image-id="${id}"]`);
  },

  updateBackInStock(variant, available) {
    const box = document.querySelector('[data-back-in-stock]');
    if (!box) return;
    const done = box.hasAttribute('data-back-in-stock-done');
    if (!done) box.hidden = !variant || available !== false;

    const tag = box.querySelector('[data-back-in-stock-tag]');
    if (tag && variant?.id) {
      const handle = document.querySelector('[data-product-handle]')?.dataset.productHandle;
      tag.value = handle
        ? `back-in-stock-${variant.id},back-in-stock--${handle}--${variant.id}`
        : `back-in-stock-${variant.id}`;
    }

    const label = box.querySelector('[data-back-in-stock-variant]');
    if (!label) return;
    const title = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
    label.textContent = title;
    label.hidden = !title;
  }
};

/* ============================================================
   TOASTS
   ============================================================ */

RikkiMode.toasts = {
  container: null,
  init() {
    if (this.container) return;
    this.container = document.createElement('div');
    this.container.className = 'toast-container';
    document.body.appendChild(this.container);
  },
  show(message, type = '') {
    if (!this.container) this.init();
    const toast = document.createElement('div');
    toast.className = `toast${type ? ` toast--${type}` : ''}`;
    toast.textContent = message;
    this.container.appendChild(toast);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => toast.classList.add('is-visible'));
    });
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
};

/* ============================================================
   WISHLIST — localStorage
   ============================================================ */

/* ============================================================
   WISHLIST + COMPARE — localStorage
   ============================================================ */

RikkiMode.lists = {
  parse(raw) {
    try {
      const value = JSON.parse(raw || '[]');
      return Array.isArray(value) ? value : [];
    } catch { return []; }
  },
  normalize(items) {
    const list = Array.isArray(items) ? items : this.parse(items);
    return list.map(item => {
      if (item && typeof item === 'object') {
        return { id: String(item.id || ''), handle: item.handle || '' };
      }
      return { id: String(item), handle: '' };
    }).filter(item => item.id || item.handle);
  }
};

RikkiMode.wishlist = {
  key: 'rikki_wishlist',
  getItems() {
    return RikkiMode.lists.normalize(localStorage.getItem(this.key));
  },
  save(items) {
    localStorage.setItem(this.key, JSON.stringify(items));
    this.syncButtons();
    this.updateCount();
  },
  has(id, handle) {
    const sid = String(id || '');
    return this.getItems().some(item => (sid && item.id === sid) || (handle && item.handle === handle));
  },
  toggle(id, handle) {
    const items = this.getItems();
    const sid = String(id || '');
    const idx = items.findIndex(item => (sid && item.id === sid) || (handle && item.handle === handle));
    if (idx === -1) items.push({ id: sid, handle: handle || '' });
    else items.splice(idx, 1);
    this.save(items);
    return idx === -1;
  },
  init() {
    if (this._bound) return;
    this._bound = true;
    this.syncButtons();
    this.updateCount();
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();
      const added = this.toggle(btn.dataset.productId, btn.dataset.productHandle);
      btn.classList.toggle('is-active', added);
      RikkiMode.toasts.show(
        added ? (theme.strings?.wishlistAdded || 'Toegevoegd aan je verlanglijst') : (theme.strings?.wishlistRemoved || 'Verwijderd van je verlanglijst'),
        added ? 'success' : ''
      );
    });
  },
  syncButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      btn.classList.toggle('is-active', this.has(btn.dataset.productId, btn.dataset.productHandle));
    });
  },
  updateCount() {
    const count = this.getItems().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = String(count);
      el.setAttribute('data-count', String(count));
    });
  }
};

RikkiMode.compare = {
  key: 'rikki_compare',
  max: 4,
  getItems() {
    return RikkiMode.lists.normalize(localStorage.getItem(this.key));
  },
  save(items) {
    localStorage.setItem(this.key, JSON.stringify(items));
    this.syncButtons();
    this.updateCount();
  },
  has(id, handle) {
    const sid = String(id || '');
    return this.getItems().some(item => (sid && item.id === sid) || (handle && item.handle === handle));
  },
  toggle(id, handle) {
    const items = this.getItems();
    const sid = String(id || '');
    const idx = items.findIndex(item => (sid && item.id === sid) || (handle && item.handle === handle));
    if (idx === -1) {
      if (items.length >= this.max) {
        RikkiMode.toasts.show(theme.strings?.compareLimit || 'Je kunt maximaal 4 producten vergelijken', 'error');
        return null;
      }
      items.push({ id: sid, handle: handle || '' });
      this.save(items);
      return true;
    }
    items.splice(idx, 1);
    this.save(items);
    return false;
  },
  init() {
    if (this._bound) return;
    this._bound = true;
    this.syncButtons();
    this.updateCount();
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-compare-btn]');
      if (!btn) return;
      e.preventDefault();
      const added = this.toggle(btn.dataset.productId, btn.dataset.productHandle);
      if (added === null) return;
      btn.classList.toggle('is-active', added);
      RikkiMode.toasts.show(
        added ? (theme.strings?.compareAdded || 'Toegevoegd aan vergelijking') : (theme.strings?.compareRemoved || 'Verwijderd uit vergelijking'),
        added ? 'success' : ''
      );
    });
  },
  syncButtons() {
    document.querySelectorAll('[data-compare-btn]').forEach(btn => {
      btn.classList.toggle('is-active', this.has(btn.dataset.productId, btn.dataset.productHandle));
    });
  },
  updateCount() {
    const count = this.getItems().length;
    document.querySelectorAll('[data-compare-count]').forEach(el => {
      el.textContent = String(count);
      el.setAttribute('data-count', String(count));
    });
  }
};

/* ============================================================
   PRODUCT CARD — size-btn quick add
   ============================================================ */

RikkiMode.cardQuickAdd = {
  init() {
    if (this._bound) return;
    this._bound = true;
    document.addEventListener('click', async e => {
      const sizeToggle = e.target.closest('[data-card-size-toggle]');
      if (sizeToggle) {
        e.preventDefault();
        const card = sizeToggle.closest('.product-card');
        card?.classList.toggle('is-size-open');
        this.filterSizes(card);
        return;
      }

      const sizeBtn = e.target.closest('.product-card__size-btn:not(.is-unavailable)');
      const quickBtn = e.target.closest('.product-card__plus[data-variant-id], .product-card__action[data-variant-id], .product-card__quick-add[data-variant-id]');
      const btn = sizeBtn || quickBtn;
      if (!btn) return;

      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      if (btn.dataset.pending === 'true') return;
      btn.dataset.pending = 'true';
      btn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        });
        if (!res.ok) throw new Error();
        await RikkiMode.cart.fetchCount();
        if (theme.cartType === 'drawer') RikkiMode.cart.open();
        RikkiMode.toasts.show(theme.strings?.addedToCart || 'Toegevoegd aan winkelmand', 'success');
      } catch {
        RikkiMode.toasts.show('Niet gelukt, probeer opnieuw', 'error');
      } finally {
        btn.disabled = false;
        btn.dataset.pending = 'false';
      }
    });
    this.filterSizes();
  },
  filterSizes(card) {
    const cards = card ? [card] : document.querySelectorAll('.product-card');
    cards.forEach(el => {
      const color = el.querySelector('[data-card-swatch].is-active')?.dataset.color;
      const buttons = el.querySelectorAll('.product-card__size-btn[data-color]');
      if (!buttons.length) return;
      buttons.forEach(btn => {
        const show = !color || btn.dataset.color === color;
        btn.hidden = !show;
      });
    });
  }
};

/* ============================================================
   RECENTLY VIEWED
   ============================================================ */

RikkiMode.recentlyViewed = {
  key: 'rikki_recent',
  init() {
    const handle = document.body.dataset.productHandle || document.querySelector('[data-product-handle]')?.dataset.productHandle;
    if (handle) this.add(handle);
    this.render();
  },
  add(handle) {
    let ids = [];
    try { ids = JSON.parse(localStorage.getItem(this.key) || '[]'); } catch { ids = []; }
    ids = ids.filter(h => h !== handle);
    ids.unshift(handle);
    localStorage.setItem(this.key, JSON.stringify(ids.slice(0, 8)));
  },
  async render() {
    const root = document.querySelector('[data-recently-viewed]');
    if (!root) return;
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem(this.key) || '[]'); } catch { handles = []; }
    const current = document.querySelector('[data-product-handle]')?.dataset.productHandle;
    handles = handles.filter(h => h !== current).slice(0, 4);
    if (!handles.length) {
      root.closest('section')?.setAttribute('hidden', '');
      return;
    }

    try {
      const products = await Promise.all(handles.map(async handle => {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        if (!res.ok) return null;
        return res.json();
      }));
      const cards = products.filter(Boolean).map(p => {
        const img = p.featured_image || (p.images && p.images[0]) || '';
        const onSale = p.compare_at_price && p.compare_at_price > p.price;
        const handle = p.handle || '';
        return `
          <article class="product-card" data-product-id="${p.id}" data-product-handle="${RikkiMode.escapeHtml(handle)}">
            <div class="product-card__media">
              <a href="${RikkiMode.escapeHtml(p.url)}" class="product-card__media-link">
                ${img ? `<img class="product-card__img product-card__img--primary" src="${RikkiMode.imageWidth(img, 600)}" alt="${RikkiMode.escapeHtml(p.title)}" width="600" height="800" loading="lazy">` : ''}
              </a>
              <button type="button" class="product-card__plus" data-quick-view data-product-handle="${RikkiMode.escapeHtml(handle)}" aria-label="${theme.strings?.quickView || 'Snel bekijken'}">
                <span aria-hidden="true">+</span>
              </button>
            </div>
            <div class="product-card__info">
              <h3 class="product-card__title"><a href="${RikkiMode.escapeHtml(p.url)}">${RikkiMode.escapeHtml(p.title)}</a></h3>
              <div class="product-card__price">
                <span class="price${onSale ? ' price--sale' : ''}">${RikkiMode.formatMoney(p.price)}</span>
                ${onSale ? `<span class="price price--compare">${RikkiMode.formatMoney(p.compare_at_price)}</span>` : ''}
              </div>
              <div class="product-card__meta-actions">
                <button type="button" class="product-card__action" data-quick-view data-product-handle="${RikkiMode.escapeHtml(handle)}" aria-label="${theme.strings?.quickView || 'Snel bekijken'}">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/></svg>
                </button>
                <button type="button" class="product-card__action" data-wishlist-btn data-product-id="${p.id}" data-product-handle="${RikkiMode.escapeHtml(handle)}">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" stroke-width="1.5"/></svg>
                </button>
              </div>
            </div>
          </article>`;
      }).join('');
      if (!cards) {
        root.closest('section')?.setAttribute('hidden', '');
        return;
      }
      root.innerHTML = cards;
      root.closest('section')?.removeAttribute('hidden');
      RikkiMode.wishlist.syncButtons();
      RikkiMode.compare.syncButtons();
    } catch (e) {
      console.error(e);
      root.closest('section')?.setAttribute('hidden', '');
    }
  }
};

RikkiMode.recommendations = {
  init() {
    document.querySelectorAll('[data-product-recommendations]').forEach(el => {
      if (el.dataset.static === 'true') return;
      const url = el.dataset.url;
      if (!url) return;
      const limit = parseInt(el.dataset.limit, 10) || 4;
      const poolTpl = el.querySelector('template[data-rec-pool]');
      const poolCards = poolTpl ? [...poolTpl.content.querySelectorAll('.product-card')] : [];

      fetch(url)
        .then(res => res.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('[data-product-recommendations]');
          const recCards = inner ? [...inner.querySelectorAll('.product-grid .product-card')] : [];
          const picked = this.mix(recCards, poolCards, limit);
          if (!picked.length) {
            el.closest('section')?.setAttribute('hidden', '');
            return;
          }
          this.render(el, picked);
        })
        .catch(() => {
          const picked = this.mix([], poolCards, limit);
          if (picked.length) this.render(el, picked);
          else el.closest('section')?.setAttribute('hidden', '');
        });
    });
  },

  render(el, cards) {
    const nodes = [];
    if (el.dataset.title) {
      const title = document.createElement('h2');
      title.className = 'section-title';
      title.textContent = el.dataset.title;
      nodes.push(title);
    }
    const grid = document.createElement('div');
    grid.className = `product-grid product-grid--${el.dataset.columns || 4}col product-grid--mobile-slider`;
    grid.replaceChildren(...cards.map(card => document.importNode(card, true)));
    nodes.push(grid);
    el.replaceChildren(...nodes);
    RikkiMode.wishlist?.syncButtons?.();
    RikkiMode.compare?.syncButtons?.();
    RikkiMode.cardHoverImages?.init?.();
    RikkiMode.newBadges?.init?.();
  },

  shuffle(list) {
    const arr = [...list];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  },

  /* Afwisselend uit Shopify-aanbevelingen en collectieproducten, in willekeurige volgorde, zonder dubbelen. */
  mix(recCards, poolCards, limit) {
    const recs = this.shuffle(recCards);
    const pool = this.shuffle(poolCards);
    const seen = new Set();
    const picked = [];
    while (picked.length < limit && (recs.length || pool.length)) {
      for (const source of [recs, pool]) {
        if (picked.length >= limit) break;
        while (source.length) {
          const card = source.shift();
          const handle = card.dataset.productHandle;
          if (handle && seen.has(handle)) continue;
          if (handle) seen.add(handle);
          picked.push(card);
          break;
        }
      }
    }
    return picked;
  }
};

RikkiMode.collectionSort = {
  init() {
    const select = document.getElementById('sort-select');
    if (!select || select.dataset.bound === 'true') return;
    select.dataset.bound = 'true';
    select.addEventListener('change', () => {
      const form = document.getElementById('collection-filters');
      if (form) {
        const hidden = form.querySelector('input[name="sort_by"]');
        if (hidden) hidden.value = select.value;
        form.submit();
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.href = url.toString();
      }
    });
  }
};

RikkiMode.loadMore = {
  init() {
    const btn = document.getElementById('load-more-btn');
    if (!btn || btn.dataset.bound === 'true') return;
    btn.dataset.bound = 'true';
    btn.addEventListener('click', async () => {
      const next = btn.dataset.nextUrl;
      if (!next) return;
      btn.disabled = true;
      btn.classList.add('btn--loading');
      try {
        const res = await fetch(next);
        if (!res.ok) throw new Error();
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('.collection-main .product-grid');
        const grid = document.querySelector('.collection-main .product-grid');
        if (incoming && grid) {
          incoming.querySelectorAll('.product-card').forEach(card => grid.appendChild(card));
        }
        const nextBtn = doc.getElementById('load-more-btn');
        if (nextBtn?.dataset.nextUrl) {
          btn.dataset.nextUrl = nextBtn.dataset.nextUrl;
          btn.disabled = false;
        } else {
          btn.remove();
        }
        RikkiMode.wishlist.syncButtons();
        RikkiMode.compare.syncButtons();
        RikkiMode.cardHoverImages.init();
      } catch {
        btn.disabled = false;
        RikkiMode.toasts.show('Kon geen extra producten laden', 'error');
      } finally {
        btn.classList.remove('btn--loading');
      }
    });
  }
};

RikkiMode.cardHoverImages = {
  init() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (!this._bound) {
      this._bound = true;
      document.addEventListener('pointerover', e => {
        this.load(e.target.closest?.('.product-card')?.querySelector('img[data-hover-src]'));
      }, { passive: true });
    }
    if (document.readyState === 'complete') this.observe();
    else window.addEventListener('load', () => this.observe(), { once: true });
  },
  load(img) {
    if (!img?.dataset.hoverSrc) return;
    if (img.dataset.hoverSrcset) img.srcset = img.dataset.hoverSrcset;
    img.src = img.dataset.hoverSrc;
    delete img.dataset.hoverSrc;
    delete img.dataset.hoverSrcset;
  },
  observe() {
    if (!('IntersectionObserver' in window)) return;
    this.observer ??= new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        this.load(entry.target);
        this.observer.unobserve(entry.target);
      });
    }, { rootMargin: '200px 0px' });
    document.querySelectorAll('img[data-hover-src]').forEach(img => this.observer.observe(img));
  }
};

RikkiMode.cardSwatches = {
  init() {
    if (this._bound) return;
    this._bound = true;
    document.addEventListener('click', e => {
      const swatch = e.target.closest('.product-card [data-card-swatch]');
      if (!swatch) return;
      e.preventDefault();
      const card = swatch.closest('.product-card');
      const img = card?.querySelector('.product-card__img--primary');
      const link = card?.querySelector('.product-card__title a, .product-card__media-link');
      if (swatch.dataset.image && img) {
        img.src = swatch.dataset.image;
        img.srcset = swatch.dataset.srcset || swatch.dataset.image;
      }
      if (swatch.dataset.url && link) {
        card.querySelectorAll('a[href]').forEach(a => {
          if (a.classList.contains('product-card__media-link') || a.closest('.product-card__title')) {
            a.href = swatch.dataset.url;
          }
        });
      }
      card.querySelectorAll('[data-card-swatch]').forEach(s => {
        s.classList.remove('is-active');
        s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('is-active');
      swatch.setAttribute('aria-pressed', 'true');
      RikkiMode.cardQuickAdd.filterSizes?.(card);
    });
  }
};

RikkiMode.newBadges = {
  init() {
    const handle = theme.newBadgeCollection;
    const limit = parseInt(theme.newLaunchCount, 10) || 200;
    if (!handle) return;
    const key = `rikki_new_ids_${handle}_${limit}`;
    try {
      const cached = sessionStorage.getItem(key);
      if (cached) {
        this.apply(JSON.parse(cached));
        return;
      }
    } catch (e) { /* private mode */ }
    fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=${Math.min(250, limit)}`)
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .then(data => {
        const ids = (data.products || []).slice(0, limit).map(p => String(p.id));
        try { sessionStorage.setItem(key, JSON.stringify(ids)); } catch (e) { /* ignore */ }
        this.apply(ids);
      })
      .catch(() => {});
  },

  apply(ids) {
    if (!ids?.length) return;
    const set = new Set(ids.map(String));
    const label = theme.strings?.newBadge || 'NIEUW!';
    document.querySelectorAll('[data-product-id]').forEach(el => {
      if (el.hasAttribute('data-hide-new')) return;
      if (!set.has(String(el.dataset.productId))) return;
      const box = el.querySelector('.product-card__badges');
      if (!box || box.querySelector('.badge--new, .badge--custom, .badge--soldout')) return;
      box.insertAdjacentHTML('afterbegin', `<span class="badge badge--new badge--ribbon">${label}</span>`);
    });
  }
};

RikkiMode.sizeGuide = {
  init() {
    this.drawer = document.getElementById('size-guide-drawer');
    if (this._bound) return;
    this._bound = true;
    document.querySelectorAll('[data-size-guide-open]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        this.open();
      });
    });
    this.drawer?.querySelectorAll('[data-size-guide-close]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawer?.getAttribute('aria-hidden') === 'false') this.close();
    });
  },
  open() {
    const accordion = document.querySelector('[data-size-guide-accordion]');
    if (accordion) {
      if (accordion.getAttribute('aria-expanded') !== 'true') accordion.click();
      document.getElementById('size-guide-accordion')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'false');
    RikkiMode.lock.add();
  },
  close() {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'true');
    RikkiMode.lock.remove();
  }
};

RikkiMode.footerAccordion = {
  init() {
    if (window.matchMedia('(min-width: 768px)').matches) return;
    document.querySelectorAll('[data-footer-col] .footer__col-toggle').forEach(btn => {
      if (btn.dataset.bound === 'true') return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
      });
    });
  }
};

RikkiMode.quickView = {
  init() {
    if (this._bound) return;
    this._bound = true;
    this.el = document.getElementById('quick-view');
    this.body = document.getElementById('quick-view-body');
    if (!this.el) return;

    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-quick-view]');
      if (btn) {
        e.preventDefault();
        this.open(btn.dataset.productHandle);
        return;
      }
      if (e.target.closest('[data-quick-view-close]')) this.close();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !this.el.hidden) this.close();
    });
  },

  close() {
    if (!this.el) return;
    this.el.hidden = true;
    this.el.setAttribute('aria-hidden', 'true');
    RikkiMode.lock.remove();
  },

  async open(handle) {
    if (!handle || !this.body) return;
    this.el.hidden = false;
    this.el.setAttribute('aria-hidden', 'false');
    RikkiMode.lock.add();
    this.body.innerHTML = '<p style="padding:2rem;text-align:center">Laden...</p>';

    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) throw new Error();
      const product = await res.json();
      this.render(product);
    } catch {
      this.body.innerHTML = '<p style="padding:2rem">Kon product niet laden. <a href="/products/' + RikkiMode.escapeHtml(handle) + '">Bekijk product</a></p>';
    }
  },

  imageSrc(image) {
    return typeof image === 'string' ? image : (image?.src || '');
  },

  render(product) {
    const images = (product.images || []).map(image => this.imageSrc(image)).filter(Boolean);
    const img = this.imageSrc(product.featured_image) || images[0] || '';
    const variants = product.variants || [];
    const options = (product.options || []).map(option => (typeof option === 'string' ? option : option?.name) || '');
    const hasOptions = options.length > 0 && !(variants.length <= 1 && (options[0] === 'Title' || variants[0]?.title === 'Default Title'));
    const compare = product.compare_at_price && product.compare_at_price > product.price
      ? `<span class="price price--compare">${RikkiMode.formatMoney(product.compare_at_price)}</span>`
      : '';
    const desc = product.description ? `<div class="quick-view__desc">${product.description}</div>` : '';
    const thumbs = images.length > 1
      ? `<div class="quick-view__thumbs">${images.slice(0, 6).map((src, i) => `
          <button type="button" class="quick-view__thumb${i === 0 ? ' is-active' : ''}" data-qv-thumb="${RikkiMode.imageWidth(src, 900)}">
            <img src="${RikkiMode.imageWidth(src, 160)}" alt="">
          </button>`).join('')}</div>`
      : '';
    const optionHtml = hasOptions
      ? options.map((name, idx) => {
          let values = [...new Set(variants.map(v => v.options[idx]).filter(Boolean))];
          if (RikkiMode.isSizeOption(name)) values = RikkiMode.sortSizes(values);
          return `<div class="quick-view__group" data-option-index="${idx}">
            <p class="form-label">${RikkiMode.escapeHtml(name)}</p>
            <div class="quick-view__options">
              ${values.map(val => `<button type="button" class="quick-view__opt" data-value="${RikkiMode.escapeHtml(val)}">${RikkiMode.escapeHtml(val)}</button>`).join('')}
            </div>
          </div>`;
        }).join('')
      : '';

    this.body.innerHTML = `
      <div class="quick-view__layout">
        <div class="quick-view__gallery">
          <div class="quick-view__img-wrap">
            ${img ? `<img class="quick-view__img" src="${RikkiMode.imageWidth(img, 900)}" alt="${RikkiMode.escapeHtml(product.title)}">` : ''}
          </div>
          ${thumbs}
        </div>
        <div>
          <h2 class="quick-view__title" id="quick-view-title">${RikkiMode.escapeHtml(product.title)}</h2>
          <p class="quick-view__price">
            <span class="price" data-qv-price>${RikkiMode.formatMoney(product.price)}</span>
            ${compare}
          </p>
          ${desc}
          ${optionHtml}
          <div class="quick-view__qty">
            <label class="form-label" for="qv-qty">${theme.strings?.quantity || 'Aantal'}</label>
            <div class="quantity-selector">
              <button type="button" class="quantity-btn" data-qv-qty="down" aria-label="Minder">−</button>
              <input type="number" id="qv-qty" class="quantity-input" value="1" min="1">
              <button type="button" class="quantity-btn" data-qv-qty="up" aria-label="Meer">+</button>
            </div>
          </div>
          <div class="quick-view__buy">
            <button type="button" class="btn btn--primary" data-quick-view-add disabled>${theme.strings?.chooseOptions || 'Kies eerst je opties'}</button>
            <button type="button" class="quick-view__icon-btn" data-wishlist-btn data-product-id="${product.id}" data-product-handle="${RikkiMode.escapeHtml(product.handle)}" aria-label="${theme.strings?.wishlistAdd || 'Op verlanglijst'}">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" stroke-width="1.5"/></svg>
            </button>
            <button type="button" class="quick-view__icon-btn" data-compare-btn data-product-id="${product.id}" data-product-handle="${RikkiMode.escapeHtml(product.handle)}" aria-label="${theme.strings?.compareAdd || 'Vergelijken'}">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17 1v6h-6M21 13A8 8 0 0 1 7.2 19.4L3 17M7 23v-6h6M3 11A8 8 0 0 1 16.8 4.6L21 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
          </div>
          <p class="quick-view__stock" data-qv-stock></p>
          <a class="quick-view__link" href="${RikkiMode.escapeHtml(product.url)}">${theme.strings?.viewProduct || 'Bekijk product'}</a>
        </div>
      </div>`;

    this.product = product;
    this.hasOptions = hasOptions;
    this.qty = 1;
    this.selected = options.map(() => null);
    this.body.querySelectorAll('.quick-view__group').forEach(group => {
      const only = group.querySelectorAll('.quick-view__opt');
      if (only.length === 1) {
        only[0].classList.add('is-active');
        this.selected[parseInt(group.dataset.optionIndex, 10)] = only[0].dataset.value;
      }
    });
    this.body.querySelectorAll('.quick-view__opt').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.closest('.quick-view__group');
        group.querySelectorAll('.quick-view__opt').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.selected[parseInt(group.dataset.optionIndex, 10)] = btn.dataset.value;
        this.syncVariant();
      });
    });
    this.body.querySelectorAll('[data-qv-thumb]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.body.querySelectorAll('[data-qv-thumb]').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        const main = this.body.querySelector('.quick-view__img');
        if (main) main.src = btn.dataset.qvThumb;
      });
    });
    this.body.querySelectorAll('[data-qv-qty]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = this.body.querySelector('#qv-qty');
        const next = Math.max(1, parseInt(input.value || '1', 10) + (btn.dataset.qvQty === 'up' ? 1 : -1));
        input.value = String(next);
        this.qty = next;
      });
    });
    this.body.querySelector('#qv-qty')?.addEventListener('change', e => {
      this.qty = Math.max(1, parseInt(e.target.value || '1', 10));
      e.target.value = String(this.qty);
    });
    this.body.querySelector('[data-quick-view-add]')?.addEventListener('click', () => this.add());
    RikkiMode.wishlist.syncButtons();
    RikkiMode.compare.syncButtons();
    this.syncVariant();
  },

  syncVariant() {
    if (!this.product) return;
    const variants = this.product.variants || [];
    const matched = variants.find(v => this.selected.every((val, i) => !val || v.options[i] === val));
    this.matched = this.hasOptions ? (this.selected.every(Boolean) ? matched : null) : variants[0];
    this.body.querySelectorAll('.quick-view__group').forEach(group => {
      const idx = parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll('.quick-view__opt').forEach(btn => {
        const available = variants.some(v => v.available && v.options[idx] === btn.dataset.value &&
          this.selected.every((val, i) => i === idx || !val || v.options[i] === val));
        btn.classList.toggle('is-unavailable', !available);
      });
    });
    const addBtn = this.body.querySelector('[data-quick-view-add]');
    const stock = this.body.querySelector('[data-qv-stock]');
    const price = this.body.querySelector('[data-qv-price]');
    if (this.matched && price) price.textContent = RikkiMode.formatMoney(this.matched.price);
    const variantImage = this.imageSrc(this.matched?.featured_image);
    if (variantImage) {
      const main = this.body.querySelector('.quick-view__img');
      if (main) main.src = RikkiMode.imageWidth(variantImage, 900);
    }
    if (stock) {
      stock.classList.toggle('is-out', !this.matched?.available);
      stock.textContent = this.matched
        ? (this.matched.available ? (theme.strings?.inStock || 'Op voorraad') : (theme.strings?.soldOut || 'Uitverkocht'))
        : '';
    }
    if (!addBtn) return;
    if (!this.matched) {
      addBtn.disabled = true;
      addBtn.textContent = theme.strings?.chooseOptions || 'Kies eerst je opties';
      return;
    }
    addBtn.disabled = !this.matched.available;
    addBtn.textContent = this.matched.available
      ? (theme.strings?.addToCart || 'In winkelmand')
      : (theme.strings?.soldOut || 'Uitverkocht');
  },

  async add() {
    if (!this.matched?.id || !this.matched.available) {
      RikkiMode.toasts.show(theme.strings?.selectSize || 'Selecteer eerst een maat', 'error');
      return;
    }
    const qty = Math.max(1, parseInt(this.body.querySelector('#qv-qty')?.value || this.qty || 1, 10));
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.matched.id, quantity: qty })
      });
      if (!res.ok) throw new Error();
      await RikkiMode.cart.fetchCount();
      this.close();
      if (theme.cartType === 'drawer') RikkiMode.cart.open();
      RikkiMode.toasts.show(theme.strings?.addedToCart || 'Toegevoegd aan winkelmand', 'success');
    } catch {
      RikkiMode.toasts.show('Niet gelukt, probeer opnieuw', 'error');
    }
  }
};

RikkiMode.listPages = {
  async init() {
    await this.render('[data-wishlist-grid]', RikkiMode.wishlist.getItems(), theme.strings?.wishlistEmpty || 'Je verlanglijst is leeg');
    await this.render('[data-compare-grid]', RikkiMode.compare.getItems(), theme.strings?.compareEmpty || 'Je hebt nog geen producten om te vergelijken', true);
  },
  async render(selector, items, emptyText, asTable) {
    const root = document.querySelector(selector);
    if (!root) return;
    const handles = items.map(item => item.handle).filter(Boolean);
    if (!handles.length) {
      root.innerHTML = `<div class="utility-empty"><p>${RikkiMode.escapeHtml(emptyText)}</p></div>`;
      return;
    }
    const products = (await Promise.all(handles.map(async handle => {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      return res.ok ? res.json() : null;
    }))).filter(Boolean);

    if (asTable) {
      root.innerHTML = `<table class="compare-table"><thead><tr>${products.map(p => `<th>${RikkiMode.escapeHtml(p.title)}</th>`).join('')}</tr></thead>
        <tbody>
          <tr>${products.map(p => `<td><img src="${RikkiMode.imageWidth(p.featured_image || (p.images && p.images[0]) || '', 500)}" alt="${RikkiMode.escapeHtml(p.title)}"></td>`).join('')}</tr>
          <tr>${products.map(p => `<td>${RikkiMode.formatMoney(p.price)}</td>`).join('')}</tr>
          <tr>${products.map(p => `<td><a class="btn btn--primary btn--full" href="${RikkiMode.escapeHtml(p.url)}">${theme.strings?.viewProduct || 'Bekijk product'}</a></td>`).join('')}</tr>
        </tbody></table>`;
      return;
    }

    root.classList.add('product-grid', 'product-grid--4col');
    root.innerHTML = products.map(p => {
      const img = p.featured_image || (p.images && p.images[0]) || '';
      return `<article class="product-card" data-product-handle="${RikkiMode.escapeHtml(p.handle)}">
        <div class="product-card__media">
          <a href="${RikkiMode.escapeHtml(p.url)}" class="product-card__media-link">
            ${img ? `<img class="product-card__img product-card__img--primary" src="${RikkiMode.imageWidth(img, 600)}" alt="${RikkiMode.escapeHtml(p.title)}" loading="lazy" width="600" height="800">` : ''}
          </a>
        </div>
        <div class="product-card__info">
          <h3 class="product-card__title"><a href="${RikkiMode.escapeHtml(p.url)}">${RikkiMode.escapeHtml(p.title)}</a></h3>
          <div class="product-card__price"><span class="price">${RikkiMode.formatMoney(p.price)}</span></div>
        </div>
      </article>`;
    }).join('');
    RikkiMode.wishlist.syncButtons();
  }
};

RikkiMode.ticker = {
  init() {
    document.querySelectorAll('[data-ticker]').forEach(viewport => {
      const track = viewport.querySelector('[data-ticker-track]');
      const group = track?.querySelector('.announcement-bar__group');
      if (!track || !group) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        track.style.animation = 'none';
        track.querySelectorAll('.announcement-bar__group[aria-hidden="true"]').forEach(el => { el.hidden = true; });
        return;
      }
      const width = group.offsetWidth;
      if (width > 0) {
        track.style.setProperty('--ticker-distance', `-${width}px`);
      }
    });
  }
};

RikkiMode.helpWidget = {
  init() {
    const root = document.querySelector('[data-help-widget]');
    if (!root || root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';
    const toggle = document.getElementById('help-widget-toggle');
    const panel = document.getElementById('help-widget-panel');
    const close = () => {
      if (!panel) return;
      panel.hidden = true;
      toggle?.setAttribute('aria-expanded', 'false');
    };
    toggle?.addEventListener('click', () => {
      const open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) panel.querySelector('a, button')?.focus();
    });
    root.querySelector('[data-help-close]')?.addEventListener('click', close);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && panel && !panel.hidden) {
        close();
        toggle?.focus();
      }
    });
  }
};

RikkiMode.newsletterPopup = {
  init() {
    const el = document.getElementById('newsletter-popup');
    if (!el || el.dataset.bound === 'true') return;
    el.dataset.bound = 'true';
    const delay = Math.max(8, parseInt(el.dataset.delay || '20', 10)) * 1000;
    if (document.body.classList.contains('template-cart') || document.body.classList.contains('template-password')) return;
    const days = parseInt(el.dataset.days || '14', 10);
    const mode = el.dataset.mode || 'days';
    const key = 'rikki_nl_popup';
    const submittedKey = 'rikki_nl_popup_submitted';
    let justSubmitted = false;
    try {
      justSubmitted = sessionStorage.getItem(submittedKey) === '1';
      sessionStorage.removeItem(submittedKey);
    } catch (e) {}

    const open = () => {
      if (document.querySelector('.shopify-pc__banner, #shopify-pc__banner, .cookie-banner, [data-cookie-banner]')) {
        el.style.zIndex = '180';
      }
      el.hidden = false;
      el.setAttribute('aria-hidden', 'false');
      RikkiMode.lock.add();
      el.querySelector('input, button:not(.newsletter-popup__close)')?.focus();
    };

    const close = (subscribed) => {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      RikkiMode.lock.remove();
      try {
        localStorage.setItem(key, JSON.stringify({
          subscribed: !!subscribed,
          closed: true,
          closedAt: Date.now()
        }));
      } catch (e) {}
    };

    const subscribed = !!el.querySelector('.newsletter-popup__success');
    el.querySelectorAll('[data-popup-close]').forEach(btn => btn.addEventListener('click', () => close(subscribed)));
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !el.hidden) close(subscribed);
    });
    el.querySelector('form')?.addEventListener('submit', () => {
      try { sessionStorage.setItem(submittedKey, '1'); } catch (e) {}
    });

    const copyBtn = el.querySelector('[data-popup-copy]');
    copyBtn?.addEventListener('click', () => {
      const code = el.querySelector('[data-popup-code]')?.textContent.trim();
      if (!code || !navigator.clipboard) return;
      navigator.clipboard.writeText(code).then(() => {
        copyBtn.textContent = copyBtn.dataset.copiedLabel || copyBtn.textContent;
      }).catch(() => {});
    });

    if (subscribed) {
      try {
        localStorage.setItem(key, JSON.stringify({ subscribed: true, closed: true, closedAt: Date.now() }));
      } catch (e) {}
      if (justSubmitted) open();
      return;
    }
    if (justSubmitted && el.querySelector('.newsletter-popup__errors')) {
      open();
      return;
    }

    try {
      const stored = JSON.parse(localStorage.getItem(key) || '{}');
      if (stored.subscribed) return;
      if (stored.closed) {
        if (mode === 'session') return;
        const closedAt = stored.closedAt || 0;
        if (Date.now() - closedAt < days * 86400000) return;
      }
    } catch (e) {}

    window.setTimeout(open, delay);
  }
};

RikkiMode.scrollIndicator = {
  init() {
    const btn = document.getElementById('scroll-indicator');
    if (!btn) return;
    const grid = document.querySelector('.collection-main .product-grid');
    if (!grid) return;
    const update = () => {
      const remaining = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
      const show = remaining > 240 && window.scrollY < 80;
      btn.hidden = !show;
    };
    btn.addEventListener('click', () => {
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    window.addEventListener('scroll', update, { passive: true });
    update();
  }
};

RikkiMode.scrollToTop = {
  init() {
    const btn = document.getElementById('scroll-to-top');
    if (!btn || btn.dataset.bound === 'true') return;
    btn.dataset.bound = 'true';
    const progress = btn.querySelector('[data-scroll-progress]');
    const threshold = Number(theme.scrollTopOffset) || 400;
    const circumference = 100;

    const update = () => {
      const doc = document.documentElement;
      const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
      const scrolled = Math.min(1, Math.max(0, window.scrollY / scrollable));
      const show = window.scrollY > threshold;
      btn.hidden = !show;
      btn.classList.toggle('is-visible', show);
      btn.setAttribute('aria-hidden', String(!show));
      if (progress) {
        progress.style.strokeDashoffset = String(circumference * (1 - scrolled));
      }
    };

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }
};

RikkiMode.scrollRestore = {
  init() {
    if (!document.querySelector('.collection-main')) return;
    const key = `rikki_scroll_${window.location.pathname}${window.location.search}`;
    window.addEventListener('pagehide', () => {
      try { sessionStorage.setItem(key, String(window.scrollY)); } catch (e) {}
    });
    const saved = sessionStorage.getItem(key);
    if (saved) {
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10) || 0));
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  RikkiMode.init();
  RikkiMode.wishlist.init();
  RikkiMode.compare.init();
  RikkiMode.cardQuickAdd.init();
  RikkiMode.listPages.init();
});

document.addEventListener('shopify:section:load', () => {
  RikkiMode.lock.reset();
  RikkiMode.header.init();
  RikkiMode.mobileNav.init();
  RikkiMode.search.init();
  RikkiMode.accordion.init();
  RikkiMode.productGallery.init();
  RikkiMode.stickyATC.init();
  RikkiMode.filterSidebar.init();
  RikkiMode.quantitySelector.init();
  RikkiMode.variantSelector.init();
  RikkiMode.collectionSort.init();
  RikkiMode.loadMore.init();
  RikkiMode.recommendations.init();
  RikkiMode.sizeGuide.init();
  RikkiMode.footerAccordion.init();
  RikkiMode.ticker.init();
  RikkiMode.helpWidget.init();
});
